import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { appStore } from '../app/state'
import { openSearchHit, search, searchHitHref, searchLabels, type SearchResult, type SearchKind } from '../domain/search'

export default function GlobalSearch({ role }: { role: string }) {
  const [q, setQ] = useState('')
  const [result, setResult] = useState<SearchResult | null>(null)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (q.trim().length < 2 || !open) {
      setResult(null)
      setError('')
      return
    }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      void search(q, 'all', 1, controller.signal)
        .then((value) => { setResult(value); setError('') })
        .catch((reason: Error) => {
          if (!controller.signal.aborted) setError(reason.message)
        })
    }, 250)
    return () => { window.clearTimeout(timer); controller.abort() }
  }, [q, open])

  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [])

  return <div className="global-search" ref={root}>
    <form onSubmit={(event) => {
      event.preventDefault()
      if (q.trim().length < 2) return
      setOpen(false)
      appStore.navigate('search', { q: q.trim(), tab: 'all', page: '1' })
    }}>
      <Search size={19} aria-hidden="true" />
      <input aria-label="Глобальный поиск" type="search" placeholder="Техника, работа или деталь" value={q}
        onFocus={() => setOpen(true)} onChange={(event) => { setQ(event.target.value); setOpen(true) }}
        onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false) }}
        aria-expanded={open && q.trim().length >= 2} aria-controls="global-search-results" autoComplete="off" />
      {q && <button type="button" aria-label="Очистить поиск" onClick={() => { setQ(''); setResult(null) }}><X size={16} /></button>}
    </form>
    {open && q.trim().length >= 2 && <div id="global-search-results" className="global-search-results" role="region" aria-label="Результаты поиска">
      {error && <p role="alert">{error}</p>}
      {!error && !result && <p>Ищем…</p>}
      {!error && result && Object.entries(searchLabels).map(([kind, label]) => {
        const hits = result.groups[kind as SearchKind]
        if (!hits.length) return null
        return <section key={kind}><h3>{label}</h3>{hits.map((hit) => <a key={hit.kind + hit.id} href={searchHitHref(hit, role)} onClick={(event) => { if (event.metaKey || event.ctrlKey || event.shiftKey) return; event.preventDefault(); setOpen(false); openSearchHit(hit, role) }}>
          <strong>{hit.code || hit.title}</strong><span>{hit.code ? hit.title : hit.subtitle}</span>
        </a>)}</section>
      })}
      {!error && result && result.total === 0 && <p>Совпадений нет</p>}
      <button className="global-search-all" type="button" onClick={() => { setOpen(false); appStore.navigate('search', { q: q.trim(), tab: 'all', page: '1' }) }}>Все результаты →</button>
    </div>}
  </div>
}
